"use strict";
/**
 * Catalog Units — Catálogo A
 *
 * The purchase and sale units QVET accepts on a catalog item, taken from §9 of the
 * HVP naming guidelines (`resources/notes/nomenclatura`).
 *
 * QVET stores **two** fields per unit — the full name (`UnitatCompra`) and its
 * abbreviation (`UnitatCompraAbrev`) — while the app has only ever stored the name.
 * This catalog is the single source for the missing half.
 *
 * **Deliberate deviation from `enums.md`:** values are the exact strings QVET stores
 * (`FRASCO`, not `frasco`), because they are written back verbatim to an external
 * system. Same exception the rule grants to the other QVET enums.
 *
 * The table also constrains *where* a unit is legal: a BOLSA is never sold, a TABLETA
 * is never purchased. See {@link isValidPurchaseUnit} / {@link isValidSaleUnit}.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CATALOG_UNIT_LABELS = exports.SALE_UNITS = exports.PURCHASE_UNITS = exports.CATALOG_UNIT_VALUES = exports.UNIT_CATALOG = exports.CatalogUnit = void 0;
exports.toCatalogUnit = toCatalogUnit;
exports.getUnitAbbreviation = getUnitAbbreviation;
exports.isValidPurchaseUnit = isValidPurchaseUnit;
exports.isValidSaleUnit = isValidSaleUnit;
var CatalogUnit;
(function (CatalogUnit) {
    CatalogUnit["CAJA"] = "CAJA";
    CatalogUnit["FRASCO"] = "FRASCO";
    CatalogUnit["DOSIS"] = "DOSIS";
    CatalogUnit["PIEZA"] = "PIEZA";
    CatalogUnit["TUBO"] = "TUBO";
    CatalogUnit["PAQUETE"] = "PAQUETE";
    CatalogUnit["ROLLO"] = "ROLLO";
    CatalogUnit["BOLSA"] = "BOLSA";
    CatalogUnit["BIDON"] = "BIDON";
    CatalogUnit["MULTIPRESENTACION"] = "MULTIPRESENTACION";
    CatalogUnit["TABLETA"] = "TABLETA";
    CatalogUnit["SOBRE"] = "SOBRE";
    CatalogUnit["ML"] = "ML";
    CatalogUnit["L"] = "L";
    CatalogUnit["G"] = "G";
    CatalogUnit["PERCENT"] = "%";
})(CatalogUnit || (exports.CatalogUnit = CatalogUnit = {}));
/**
 * Catálogo A §9.1 — the only units admitted, and the field each one may occupy.
 */
exports.UNIT_CATALOG = {
    [CatalogUnit.CAJA]: {
        unit: CatalogUnit.CAJA, abbreviation: 'CJ', allowedAsPurchase: true, allowedAsSale: true,
        description: 'Envase rígido que agrupa varias unidades',
    },
    [CatalogUnit.FRASCO]: {
        unit: CatalogUnit.FRASCO, abbreviation: 'FCO', allowedAsPurchase: true, allowedAsSale: true,
        description: 'Recipiente unitario de contenido, sea cual sea su tamaño y su forma: vial, ampolleta, botella, bote, gotero, frasco de tabletas o de polvo, y bolsa flexible de suero. Desde 3 L, BIDON',
    },
    [CatalogUnit.DOSIS]: {
        unit: CatalogUnit.DOSIS, abbreviation: 'DS', allowedAsPurchase: true, allowedAsSale: true,
        description: 'Lo que se aplica de una vez, lleve uno o varios frascos. Las vacunas combinadas son liofilizado mas su diluyente: dos frascos, una dosis',
    },
    [CatalogUnit.PIEZA]: {
        unit: CatalogUnit.PIEZA, abbreviation: 'PZA', allowedAsPurchase: true, allowedAsSale: true,
        description: 'Unidad genérica de lo que se cuenta',
    },
    [CatalogUnit.TUBO]: {
        unit: CatalogUnit.TUBO, abbreviation: 'TUB', allowedAsPurchase: true, allowedAsSale: true,
        description: 'Envase de cremas, geles y ungüentos',
    },
    [CatalogUnit.PAQUETE]: {
        unit: CatalogUnit.PAQUETE, abbreviation: 'PQT', allowedAsPurchase: true, allowedAsSale: true,
        description: 'Agrupación de piezas sueltas, sin caja rígida',
    },
    [CatalogUnit.ROLLO]: {
        unit: CatalogUnit.ROLLO, abbreviation: 'ROL', allowedAsPurchase: true, allowedAsSale: true,
        description: 'Etiquetas, papel',
    },
    [CatalogUnit.BOLSA]: {
        unit: CatalogUnit.BOLSA, abbreviation: 'BOL', allowedAsPurchase: true, allowedAsSale: false,
        description: 'Algodón, gasas, torundas',
    },
    [CatalogUnit.BIDON]: {
        unit: CatalogUnit.BIDON, abbreviation: 'BID', allowedAsPurchase: true, allowedAsSale: false,
        description: 'Recipiente grande, de 3 L en adelante. Comprende garrafón y cubeta',
    },
    [CatalogUnit.MULTIPRESENTACION]: {
        unit: CatalogUnit.MULTIPRESENTACION, abbreviation: 'MPR', allowedAsPurchase: true, allowedAsSale: false,
        description: 'El envase de compra varía por proveedor',
    },
    [CatalogUnit.TABLETA]: {
        unit: CatalogUnit.TABLETA, abbreviation: 'TAB', allowedAsPurchase: false, allowedAsSale: true,
        description: 'Comprende comprimido, gragea, pastilla y cápsula cuando no se distinga',
    },
    [CatalogUnit.SOBRE]: {
        unit: CatalogUnit.SOBRE, abbreviation: 'SB', allowedAsPurchase: false, allowedAsSale: true,
        description: 'Polvos y granulados en sobre individual',
    },
    [CatalogUnit.ML]: {
        unit: CatalogUnit.ML, abbreviation: 'ML', allowedAsPurchase: false, allowedAsSale: true,
        description: 'Se vende por mililitro',
    },
    [CatalogUnit.L]: {
        unit: CatalogUnit.L, abbreviation: 'L', allowedAsPurchase: false, allowedAsSale: true,
        description: 'Se vende por litro',
    },
    [CatalogUnit.G]: {
        unit: CatalogUnit.G, abbreviation: 'G', allowedAsPurchase: false, allowedAsSale: true,
        description: 'Se vende por gramo',
    },
    [CatalogUnit.PERCENT]: {
        unit: CatalogUnit.PERCENT, abbreviation: '%', allowedAsPurchase: false, allowedAsSale: true,
        description: 'Se fracciona en cien partes',
    },
};
exports.CATALOG_UNIT_VALUES = Object.values(CatalogUnit);
/** Units legal in `UnitatCompra` */
exports.PURCHASE_UNITS = exports.CATALOG_UNIT_VALUES.filter((u) => exports.UNIT_CATALOG[u].allowedAsPurchase);
/** Units legal in `UnitatVenta` */
exports.SALE_UNITS = exports.CATALOG_UNIT_VALUES.filter((u) => exports.UNIT_CATALOG[u].allowedAsSale);
/** `{ FRASCO: 'FCO', ... }` — for dropdowns and quick lookups */
exports.CATALOG_UNIT_LABELS = exports.CATALOG_UNIT_VALUES.reduce((acc, u) => ({ ...acc, [u]: exports.UNIT_CATALOG[u].abbreviation }), {});
/** Normalise free text to a catalog unit. Returns undefined if it is not one. */
function toCatalogUnit(value) {
    if (!value)
        return undefined;
    const normalised = value.trim().toUpperCase();
    return exports.CATALOG_UNIT_VALUES.find((u) => u === normalised);
}
/**
 * The abbreviation QVET expects for a unit name.
 *
 * Returns undefined for anything outside the catalog — callers must decide, because
 * writing a wrong abbreviation is exactly the defect the naming cleanup is fixing by hand.
 */
function getUnitAbbreviation(value) {
    const unit = toCatalogUnit(value);
    return unit ? exports.UNIT_CATALOG[unit].abbreviation : undefined;
}
function isValidPurchaseUnit(value) {
    const unit = toCatalogUnit(value);
    return unit ? exports.UNIT_CATALOG[unit].allowedAsPurchase : false;
}
function isValidSaleUnit(value) {
    const unit = toCatalogUnit(value);
    return unit ? exports.UNIT_CATALOG[unit].allowedAsSale : false;
}
